export const MENU_TRAINING_PILLAR_PATH = "/menu-training";
export const MENU_TRAINING_TEMPLATE_PATH = "/menu-training/template";
export const MENU_TRAINING_CHECKLIST_PATH = "/menu-training/checklist";
export const SERVER_TRAINING_PILLAR_PATH = "/server-training";
export const SERVER_TRAINING_CHECKLIST_PATH = "/server-training/onboarding-checklist";
export const WINE_TRAINING_PILLAR_PATH = "/wine-training";
export const WINE_TRAINING_PAIRING_GUIDE_PATH = "/wine-training/pairing-guide";
export const ALLERGEN_SAFETY_PILLAR_PATH = "/allergen-safety";
export const ALLERGEN_SAFETY_QA_GUIDE_PATH = "/allergen-safety/q-and-a-guide";

export const SHOW_CONTENT_CLUSTER_PROMOS = false;

export const MENU_TRAINING_CLUSTER_POST_SLUGS = new Set([
  "automate-menu-training",
  "clear-menu-language-reduces-kitchen-mistakes",
  "consistency-in-a-high-turnover-world",
  "hospitality-workforce-learns-differently",
  "menu-confidence-reduces-turnover",
  "menu-is-a-language",
  "servers-not-lazy-trained-the-wrong-way",
  "servers-struggling-with-menu",
  "sla-in-menu-training",
  "sla-weekly-training-calendar",
  "staff-afraid-of-not-knowing-answers",
]);

export const SERVER_TRAINING_CLUSTER_POST_SLUGS = new Set([
  "gen-z-training-expectations",
  "reduce-manager-training-burnout-without-lowering-standards",
  "stop-wasting-pre-shift-five-minute-training",
  "the-cost-of-letting-new-hires-learn-the-menu-by-guessing",
  "train-new-servers-in-24-hours",
  "training-new-hires-should-not-halt-your-entire-operation",
  "why-restaurants-lose-their-best-servers-and-how-better-menu-training-helps-you-keep-them",
]);

export const WINE_TRAINING_CLUSTER_POST_SLUGS = new Set([
  "comprehensible-input-wine-training",
  "glass-pour-sales-shouldnt-be-a-gamble",
  "great-wine-program-no-one-can-talk-about",
  "noticing-fix-wine-description-errors",
  "output-feedback-wine-confidence",
  "speak-about-wine-like-food",
  "task-based-learning-wine-training",
  "teach-wine-menu-like-language",
  "why-menu-fluency-increases-trust-and-check-averages",
  "your-servers-are-learning-wine-one-mistake-at-a-time",
]);

export const ALLERGEN_SAFETY_CLUSTER_POST_SLUGS = new Set([
  "clear-menu-language-reduces-kitchen-mistakes",
  "menu-miscommunication-chef",
  "servers-struggling-with-menu",
  "staff-afraid-of-not-knowing-answers",
  "stop-wasting-pre-shift-five-minute-training",
  "team-saying-different-things-about-the-same-dish",
  "whats-in-that-dish-training",
  "your-servers-are-getting-asked-the-same-questions-every-night",
]);

export const MENU_TRAINING_CLUSTER_RESOURCES = Object.freeze([
  {
    title: "Restaurant Menu Training Program",
    description: "See the full system for training staff on your menu with structured lessons, reinforcement, and manager visibility.",
    to: MENU_TRAINING_PILLAR_PATH,
    cta: "Read the guide",
  },
  {
    title: "Menu Training Template",
    description: "Get the practical PDF template for tightening onboarding, reinforcement, and manager follow-through.",
    to: MENU_TRAINING_TEMPLATE_PATH,
    cta: "Get the template",
  },
  {
    title: "Menu Training Checklist",
    description: "Use the checklist to tighten onboarding, reinforcement, and menu-knowledge consistency across shifts.",
    to: MENU_TRAINING_CHECKLIST_PATH,
    cta: "Open the checklist",
  },
]);

export const SERVER_TRAINING_CLUSTER_RESOURCES = Object.freeze([
  {
    title: "Restaurant Server Training Program",
    description: "See the full onboarding and reinforcement system for getting new servers confident faster without relying on shadow shifts alone.",
    to: SERVER_TRAINING_PILLAR_PATH,
    cta: "Read the guide",
  },
  {
    title: "Server Onboarding Checklist",
    description: "Use the checklist to tighten day-one expectations, pre-shift reinforcement, and early coaching for new hires.",
    to: SERVER_TRAINING_CHECKLIST_PATH,
    cta: "Open the checklist",
  },
]);

export const WINE_TRAINING_CLUSTER_RESOURCES = Object.freeze([
  {
    title: "Restaurant Wine Training Guide",
    description: "See the full wine-training system for teaching servers how to describe, recommend, and sell your wine list with more consistency.",
    to: WINE_TRAINING_PILLAR_PATH,
    cta: "Read the guide",
  },
  {
    title: "Wine Pairing Guide for Servers",
    description: "Use the pairing guide to tighten guest-facing recommendations, bottle confidence, and check-average opportunities.",
    to: WINE_TRAINING_PAIRING_GUIDE_PATH,
    cta: "Open the pairing guide",
  },
]);

export const ALLERGEN_SAFETY_CLUSTER_RESOURCES = Object.freeze([
  {
    title: "Restaurant Allergen Safety Guide",
    description: "See the full system for training staff to answer allergen, substitution, and dietary questions with more consistency.",
    to: ALLERGEN_SAFETY_PILLAR_PATH,
    cta: "Read the guide",
  },
  {
    title: "Allergen Q&A Guide for Servers",
    description: "Use the guide to tighten guest-facing allergy answers, safe substitutions, and escalation language across the floor.",
    to: ALLERGEN_SAFETY_QA_GUIDE_PATH,
    cta: "Open the Q&A guide",
  },
]);

export function isMenuTrainingClusterPost(post) {
  if (!post) return false;
  return MENU_TRAINING_CLUSTER_POST_SLUGS.has(String(post.slug || ""));
}

export function isServerTrainingClusterPost(post) {
  if (!post) return false;
  return SERVER_TRAINING_CLUSTER_POST_SLUGS.has(String(post.slug || ""));
}

export function isWineTrainingClusterPost(post) {
  if (!post) return false;
  return WINE_TRAINING_CLUSTER_POST_SLUGS.has(String(post.slug || ""));
}

export function isAllergenSafetyClusterPost(post) {
  if (!post) return false;
  return ALLERGEN_SAFETY_CLUSTER_POST_SLUGS.has(String(post.slug || ""));
}

export function getMenuTrainingClusterResources() {
  return [...MENU_TRAINING_CLUSTER_RESOURCES];
}

export function getServerTrainingClusterResources() {
  return [...SERVER_TRAINING_CLUSTER_RESOURCES];
}

export function getWineTrainingClusterResources() {
  return [...WINE_TRAINING_CLUSTER_RESOURCES];
}

export function getAllergenSafetyClusterResources() {
  return [...ALLERGEN_SAFETY_CLUSTER_RESOURCES];
}

export function getMenuTrainingClusterNarrative(post) {
  if (!isMenuTrainingClusterPost(post)) return null;

  return {
    eyebrow: "Menu training systems",
    intro:
      "This article is part of the Speak Your Menu menu-training cluster for operators building a more consistent onboarding and reinforcement system.",
    outro:
      "If you want the broader operating model behind this article, read the full menu training guide and use the checklist to turn it into a repeatable manager process.",
  };
}

export function getServerTrainingClusterNarrative(post) {
  if (!isServerTrainingClusterPost(post)) return null;

  return {
    eyebrow: "Server training systems",
    intro:
      "This article is part of the Speak Your Menu server-training cluster for operators improving onboarding speed, early-shift confidence, and manager coaching.",
    outro:
      "If you want the broader operating model behind this article, read the full server-training guide and use the onboarding checklist to turn it into a repeatable rollout.",
  };
}

export function getWineTrainingClusterNarrative(post) {
  if (!isWineTrainingClusterPost(post)) return null;

  return {
    eyebrow: "Wine training and sales",
    intro:
      "This article is part of the Speak Your Menu wine-training cluster for operators who want stronger wine confidence, cleaner guest recommendations, and better beverage sales execution.",
    outro:
      "If you want the broader operating model behind this article, read the full wine-training guide and use the pairing guide to turn it into repeatable table-side language.",
  };
}

export function getAllergenSafetyClusterNarrative(post) {
  if (!isAllergenSafetyClusterPost(post)) return null;

  return {
    eyebrow: "Allergen and dietary safety",
    intro:
      "This article is part of the Speak Your Menu allergen-safety cluster for operators who want more accurate dietary answers, safer substitutions, and stronger guest trust during service.",
    outro:
      "If you want the broader operating model behind this article, read the full allergen-safety guide and use the Q&A guide to turn it into repeatable table-side language.",
  };
}

export function getClusterResourcesForPost(post) {
  if (isAllergenSafetyClusterPost(post)) return getAllergenSafetyClusterResources();
  if (isWineTrainingClusterPost(post)) return getWineTrainingClusterResources();
  if (isServerTrainingClusterPost(post)) return getServerTrainingClusterResources();
  if (isMenuTrainingClusterPost(post)) return getMenuTrainingClusterResources();
  return [];
}

export function getClusterNarrativeForPost(post) {
  if (isAllergenSafetyClusterPost(post)) return getAllergenSafetyClusterNarrative(post);
  if (isWineTrainingClusterPost(post)) return getWineTrainingClusterNarrative(post);
  if (isServerTrainingClusterPost(post)) return getServerTrainingClusterNarrative(post);
  if (isMenuTrainingClusterPost(post)) return getMenuTrainingClusterNarrative(post);
  return null;
}

export function shouldShowContentClusterPromos() {
  return SHOW_CONTENT_CLUSTER_PROMOS;
}